/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import { KnowledgeGraphJsonLd } from "@/components/Schema/KnowledgeGraphJsonLd";
import { Countries } from "@/enums/countries.enum";
import { JobResponseEntity } from "@/hooks/api/generated";
import { getLocationName } from "@/utils/location-slug.util";
import {
  APP_URL,
  getBreadcrumbSchema,
  getPlatformSchema,
  getWebPageSchema,
  getWebSiteSchema,
} from "@/utils/schema-generators";

export interface IJobOverviewCardProps {
  jobDetails: JobResponseEntity;
  returnData?: boolean;
}

export function getMonthsOfExperience(exp: string): number {
  switch (exp) {
    case "0-1 years":
      return 6;
    case "1-2 years":
      return 12;
    case "2-5 years":
      return 24;
    case "more than 5 years":
      return 60;
    default:
      return 0;
  }
}

export const SalaryTypeMapping = {
  hourly: "HOUR",
  daily: "DAY",
  weekly: "WEEK",
  bi_weekly: "WEEK",
  monthly: "MONTH",
  annually: "YEAR",
} as const;

export function JobPosting({
  jobDetails,
  returnData = false,
}: IJobOverviewCardProps): JSX.Element | any {
  const employmentTypeMapping: Record<string, string> = {
    "Full-time": "FULL_TIME",
    "Part time/Temporary": "PART_TIME",
    Contract: "CONTRACTOR",
    Temporary: "TEMPORARY",
    Internship: "INTERN",
    Volunteer: "VOLUNTEER",
  };

  const employmentType =
    employmentTypeMapping[jobDetails?.type_of_contract?.trim() as string] ||
    "FULL_TIME";

  const logoUrl =
    "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1752151188/web-app-images/mygwork-logo_ojurkz.png";

  type OrganizationImageItem = {
    image?: {
      type?: string;
      name?: string;
    };
  };

  const hiringOrganizationLogo: string | undefined =
    jobDetails.organization.organizationImage.find(
      (img: OrganizationImageItem) => img?.image?.type === "logo"
    )?.image?.name;

  const organizationLogoUrl = hiringOrganizationLogo
    ? `https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1/${hiringOrganizationLogo}`
    : logoUrl;

  const socialLinks = [
    jobDetails.organization.linkedin_url,
    jobDetails.organization.twitter_url,
    jobDetails.organization.youtube_url,
    jobDetails.organization.facebook_url,
    jobDetails.organization.instagram_url,
  ].filter(Boolean);
  const sameAs = [
    `${APP_URL}/organizations/${jobDetails.organization.slug}`,
    ...socialLinks,
  ];

  if (!jobDetails.organization.bottom_link_url?.includes("mygwork.com/")) {
    sameAs.push(jobDetails.organization.bottom_link_url);
  }
  const locationData = getLocationName(
    jobDetails.locations.country as Countries,
    (jobDetails.locations as any)?.location_slug ??
      jobDetails.locations?.location
  );

  const state = (jobDetails.locations as any)?.state ?? null;
  const normalizedJobType = jobDetails.type?.trim().toLowerCase() ?? "";

  const isRemoteOrHybrid =
    normalizedJobType.includes("remote") ||
    normalizedJobType.includes("hybrid");

  const structuredData: any = {
    "@type": "JobPosting",
    "@id": `${APP_URL}/jobs/${jobDetails.slug}/#job`,
    title: jobDetails.title,
    description: returnData
      ? jobDetails.title
      : jobDetails.description || jobDetails.title,
    url: `${APP_URL}/jobs/${jobDetails.slug}`,
    identifier: {
      "@type": "PropertyValue",
      name: "myGwork",
      value: jobDetails.id,
    },
    datePosted: jobDetails.createdAt,
    validThrough: jobDetails.date_expired,
    employmentType,
    hiringOrganization: {
      "@type": "Organization",
      "@id": `${APP_URL}/organizations/${jobDetails.organization.slug}/#employer`,
      name: jobDetails.organization.name,
      logo: organizationLogoUrl,
      sameAs,
      knowsAbout: [jobDetails.organization.industry?.name].filter(Boolean),
    },
    ...(isRemoteOrHybrid && {
      jobLocationType: "TELECOMMUTE",
    }),
  };

  if (jobDetails.type === "remote") {
    structuredData.applicantLocationRequirements = {
      "@type": "Country",
      name: jobDetails.locations.country,
    };
    structuredData.jobLocation = {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry: jobDetails.locations.country,
      },
    };
  } else {
    structuredData.jobLocation = {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        ...(jobDetails.locations.location &&
          jobDetails.locations.location !== "Not Specified" &&
          locationData && {
            addressLocality: locationData,
          }),

        ...(state &&
          state.trim() !== "" &&
          state !== "Not Specified" && {
            addressRegion: state,
          }),
        addressCountry: jobDetails.locations.country,
      },
    };
  }

  if (jobDetails.salary_to) {
    const salaryValue: any = {
      "@type": "QuantitativeValue",
      unitText:
        SalaryTypeMapping[
          jobDetails.salary_type as keyof typeof SalaryTypeMapping
        ] || "YEAR",
    };

    if (
      jobDetails.salary_from &&
      jobDetails.salary_from !== jobDetails.salary_to
    ) {
      salaryValue.minValue = jobDetails.salary_from;
      salaryValue.maxValue = jobDetails.salary_to;
    } else {
      salaryValue.value = jobDetails.salary_to;
    }

    structuredData.baseSalary = {
      "@type": "MonetaryAmount",
      currency: jobDetails.salary_currency,
      value: salaryValue,
    };
  }

  if (jobDetails.years_of_experience && !returnData) {
    structuredData.experienceRequirements = {
      "@type": "OccupationalExperienceRequirements",
      monthsOfExperience: getMonthsOfExperience(jobDetails.years_of_experience),
    };
  }

  if (returnData) {
    return structuredData;
  }

  const webPageSchema: any = getWebPageSchema(
    "Jobs",
    `/jobs/${jobDetails.slug}/`
  );
  webPageSchema.mainEntity = {
    "@id": `${APP_URL}/jobs/${jobDetails.slug}/#job`,
  };
  webPageSchema.isPartOf = { "@id": `${APP_URL}/#website` };
  webPageSchema.significantLink = "https://mygwork.com/jobs";

  const schemas = [
    getWebSiteSchema(true),
    getPlatformSchema({ minimal: true }),
    getBreadcrumbSchema(
      [
        { name: "Home", url: "/" },
        { name: "Jobs", url: "/jobs" },
        { name: jobDetails.title, url: `/jobs/${jobDetails.slug}` },
      ],
      `/jobs/${jobDetails.slug}/#breadcrumb`
    ),
    structuredData,
    webPageSchema,
  ];

  return <KnowledgeGraphJsonLd schemas={schemas} />;
}
